import { useEffect, useRef, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, Send, Loader2, Mic, Square, Trash2, Paperclip, FileText, Download, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";

const ROLE_STYLE = {
  landlord: "bg-emerald-50 text-emerald-700 border-emerald-100",
  contractor: "bg-amber-50 text-amber-700 border-amber-100",
  staff: "bg-blue-50 text-blue-700 border-blue-100",
  admin: "bg-blue-50 text-blue-700 border-blue-100",
};
const ROLE_LABEL = { landlord: "Landlord", contractor: "Contractor", staff: "Project Team", admin: "Project Team", tenant: "Tenant" };

const TO_OPTIONS = {
  staff: ["Contractor", "Landlord", "Everyone"],
  admin: ["Contractor", "Landlord", "Everyone"],
  landlord: ["Project Team", "Contractor", "Everyone"],
  contractor: ["Project Team", "Landlord", "Everyone"],
};

export const ProjectThread = ({ projectId, compact = false, senderRole = "staff" }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const options = TO_OPTIONS[senderRole] || ["Everyone"];
  const [to, setTo] = useState(options[0]);
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState("");
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  const [attachFiles, setAttachFiles] = useState([]);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  const load = () => api.get(`/comments?entity_type=project&entity_id=${projectId}`)
    .then(({ data }) => setComments(data)).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [comments]);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const send = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post("/comments", { entity_type: "project", entity_id: projectId, body, addressed_to: to });
      setText("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send message"); }
    finally { setSending(false); }
  };

  const startRec = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { toast.error("Recording not supported in this browser"); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data.size) chunksRef.current.push(ev.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        streamRef.current?.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch { toast.error("Microphone access denied"); }
  };

  const stopRec = () => {
    recorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const discardVoice = () => {
    setAudioBlob(null);
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl("");
    setElapsed(0);
  };

  const sendVoice = async () => {
    if (!audioBlob) return;
    setSending(true);
    const fd = new FormData();
    fd.append("file", audioBlob, "voice-note.webm");
    fd.append("entity_type", "project");
    fd.append("entity_id", projectId);
    fd.append("addressed_to", to);
    try {
      await api.post("/comments/voice", fd, { headers: { "Content-Type": "multipart/form-data" } });
      discardVoice();
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send voice note"); }
    finally { setSending(false); }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(1, "0")}:${String(s % 60).padStart(2, "0")}`;

  const pickFile = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const valid = files.filter((f) => {
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name} is too large (max 25MB)`); return false; }
      return true;
    });
    if (valid.length) setAttachFiles((prev) => [...prev, ...valid]);
    e.target.value = "";
  };

  const removeAttach = (idx) => setAttachFiles((prev) => prev.filter((_, i) => i !== idx));

  const sendAttachment = async () => {
    if (!attachFiles.length) return;
    setSending(true);
    try {
      for (let i = 0; i < attachFiles.length; i++) {
        const fd = new FormData();
        fd.append("file", attachFiles[i]);
        fd.append("entity_type", "project");
        fd.append("entity_id", projectId);
        fd.append("addressed_to", to);
        fd.append("body", i === 0 ? text.trim() : "");
        await api.post("/comments/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setAttachFiles([]); setText("");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send file"); }
    finally { setSending(false); }
  };

  const isImage = (ct) => (ct || "").startsWith("image/");

  const canModify = (c) => user && c.author_user_id === user.user_id;
  const startEdit = (c) => { setEditingId(c.comment_id); setEditText(c.body || ""); };
  const cancelEdit = () => { setEditingId(null); setEditText(""); };
  const saveEdit = async (c) => {
    const body = editText.trim();
    if (!body) return;
    try {
      await api.patch(`/comments/${c.comment_id}`, { body });
      cancelEdit(); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update message"); }
  };
  const removeMsg = async (c) => {
    if (!window.confirm("Delete this message?")) return;
    try { await api.delete(`/comments/${c.comment_id}`); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete message"); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`project-thread-${projectId}`}>
      <p className="text-xs font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><MessageSquare size={13} /> Communication with the project team &amp; contractors</p>
      <div ref={scrollRef} className={`space-y-2 ${compact ? "max-h-40" : "max-h-56"} overflow-y-auto mb-3 pr-1`}>
        {loaded && comments.length === 0 && (
          <p className="text-xs text-slate-400">No messages yet. Post a message and everyone on this project will see it.</p>
        )}
        {comments.map((c) => (
          <div key={c.comment_id} data-testid={`project-msg-${c.comment_id}`} className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 mb-0.5 flex-wrap">
              <span className="text-xs font-medium text-slate-800">{c.author_name}</span>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${ROLE_STYLE[c.author_role] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{ROLE_LABEL[c.author_role] || c.author_role}</span>
              {c.addressed_to && <span className="text-[10px] text-slate-500">→ To: <span className="font-medium text-slate-600">{c.addressed_to}</span></span>}
              {c.edited && <span className="text-[10px] text-slate-400 italic">(edited)</span>}
              <span className="text-[10px] text-slate-400 ml-auto">{new Date(c.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
              {canModify(c) && editingId !== c.comment_id && (
                <span className="flex items-center gap-1">
                  {!c.audio_file_id && <button data-testid={`edit-msg-${c.comment_id}`} onClick={() => startEdit(c)} title="Edit" className="text-slate-300 hover:text-blue-600 p-0.5"><Pencil size={12} /></button>}
                  <button data-testid={`delete-msg-${c.comment_id}`} onClick={() => removeMsg(c)} title="Delete" className="text-slate-300 hover:text-red-500 p-0.5"><Trash2 size={12} /></button>
                </span>
              )}
            </div>
            {editingId === c.comment_id ? (
              <div className="flex items-center gap-1.5 mt-1">
                <input data-testid={`edit-input-${c.comment_id}`} value={editText} onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") cancelEdit(); }}
                  className="flex-1 rounded-md border border-slate-300 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" autoFocus />
                <button data-testid={`save-edit-${c.comment_id}`} onClick={() => saveEdit(c)} className="text-emerald-600 hover:text-emerald-700 p-1"><Check size={16} /></button>
                <button onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 p-1"><X size={16} /></button>
              </div>
            ) : c.audio_file_id ? (
              <div className="flex items-center gap-2 mt-1" data-testid={`voice-note-${c.comment_id}`}>
                <Mic size={13} className="text-blue-600 shrink-0" />
                <audio controls preload="none" src={fileUrl(c.audio_file_id)} className="h-8 max-w-full" style={{ maxWidth: "230px" }} />
              </div>
            ) : (
              <>
                {c.body && <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{c.body}</p>}
                {c.attachment_file_id && (
                  isImage(c.attachment_content_type) ? (
                    <a href={fileUrl(c.attachment_file_id)} target="_blank" rel="noreferrer" data-testid={`attachment-${c.comment_id}`} className="block mt-1.5">
                      <img src={fileUrl(c.attachment_file_id)} alt={c.attachment_name} className="max-h-40 rounded-lg border border-slate-200" />
                    </a>
                  ) : (
                    <a href={fileUrl(c.attachment_file_id)} target="_blank" rel="noreferrer" data-testid={`attachment-${c.comment_id}`}
                      className="inline-flex items-center gap-1.5 mt-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:border-blue-300 max-w-full">
                      <FileText size={13} className="text-blue-600 shrink-0" />
                      <span className="truncate">{c.attachment_name || "Attachment"}</span>
                      <Download size={12} className="text-slate-400 shrink-0" />
                    </a>
                  )
                )}
              </>
            )}
          </div>
        ))}
      </div>

      <form onSubmit={send} className="space-y-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-slate-400">Send to</span>
          <select data-testid={`project-msg-to-${projectId}`} value={to} onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600">
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <span className="text-slate-400">— everyone on the project still receives it</span>
        </div>

        {audioBlob ? (
          <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid={`voice-preview-${projectId}`}>
            <audio controls src={audioUrl} className="h-8 flex-1" />
            <button type="button" onClick={discardVoice} title="Discard" className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={16} /></button>
            <button type="button" onClick={sendVoice} disabled={sending} data-testid={`send-voice-${projectId}`}
              className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send
            </button>
          </div>
        ) : recording ? (
          <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2" data-testid={`recording-${projectId}`}>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-sm text-red-700 font-medium">Recording… {mmss(elapsed)}</span>
            <button type="button" onClick={stopRec} data-testid={`stop-voice-${projectId}`}
              className="ml-auto bg-red-600 text-white hover:bg-red-700 rounded-lg px-3 py-2 text-sm font-medium flex items-center gap-1.5"><Square size={14} /> Stop</button>
          </div>
        ) : attachFiles.length ? (
          <div className="space-y-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2" data-testid={`attach-preview-${projectId}`}>
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {attachFiles.map((f, i) => (
                <div key={`${f.name}-${i}`} className="flex items-center gap-2" data-testid={`attach-item-${projectId}-${i}`}>
                  <Paperclip size={14} className="text-blue-600 shrink-0" />
                  <span className="text-sm text-slate-700 truncate flex-1">{f.name}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{(f.size / 1024).toFixed(0)} KB</span>
                  <button type="button" onClick={() => removeAttach(i)} title="Remove" data-testid={`attach-remove-${projectId}-${i}`} className="text-slate-400 hover:text-red-500 p-1"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={() => fileInputRef.current?.click()} data-testid={`attach-more-${projectId}`}
              className="text-xs font-medium text-blue-700 hover:text-blue-800 flex items-center gap-1"><Paperclip size={12} /> Add another file</button>
            <div className="flex gap-2">
              <input data-testid={`attach-caption-${projectId}`} value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Add a message (optional)…" className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
              <button type="button" onClick={sendAttachment} disabled={sending} data-testid={`send-attach-${projectId}`}
                className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3.5 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
                {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send{attachFiles.length > 1 ? ` (${attachFiles.length})` : ""}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex gap-2">
            <textarea data-testid={`project-msg-input-${projectId}`} value={text} onChange={(e) => setText(e.target.value)}
              rows={1} placeholder="Write a message… (Enter for a new line)" className="flex-1 resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600" />
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={pickFile} data-testid={`attach-input-${projectId}`} />
            <button type="button" onClick={() => fileInputRef.current?.click()} data-testid={`attach-btn-${projectId}`} title="Attach a file"
              className="border border-slate-300 text-slate-500 hover:text-blue-700 hover:border-blue-300 rounded-lg px-3 py-2 flex items-center"><Paperclip size={16} /></button>
            <button type="button" onClick={startRec} data-testid={`voice-record-${projectId}`} title="Record voice note"
              className="border border-slate-300 text-slate-500 hover:text-blue-700 hover:border-blue-300 rounded-lg px-3 py-2 flex items-center"><Mic size={16} /></button>
            <button type="submit" data-testid={`project-msg-send-${projectId}`} disabled={sending || !text.trim()}
              className="bg-blue-700 text-white hover:bg-blue-800 rounded-lg px-3.5 py-2 text-sm font-medium flex items-center gap-1.5 disabled:opacity-50">
              {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default ProjectThread;
